import { Navigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';

export const PERMISSIONS = {
  VIEW_CALENDAR: 'viewCalendar',
  EDIT_CALENDAR: 'editCalendar',
  VIEW_CATEGORIES: 'viewCategories',
  EDIT_CATEGORIES: 'editCategories',
  VIEW_PAYMENTS: 'viewPayments',
  EDIT_PAYMENTS: 'editPayments',
  VIEW_MEMBERS: 'viewMembers',
  EDIT_MEMBERS: 'editMembers',
  VIEW_EMPLOYEES: 'viewEmployees',
  EDIT_EMPLOYEES: 'editEmployees',
  VIEW_EVENTS: 'viewEvents',
  EDIT_EVENTS: 'editEvents',
  VIEW_CONFIG: 'viewConfig',
  EDIT_CONFIG: 'editConfig',
  // Pago a instructores (pedido Paola 2026-10-09): antes salía con "Ver Pagos"
  // y lo veían todos los empleados. Sin fallback a propósito: apagado por
  // defecto hasta que un admin lo marque.
  VIEW_INSTRUCTOR_PAY: 'viewInstructorPay',
  // Vendedores de la Caribbean Sporting Clays Cup (2026-10-28). Hereda de
  // "Ver Pagos" hasta que un admin lo edite.
  VIEW_CSCC_VENDORS: 'viewCsccVendors',
  // Sorteos de boletos numerados (2026-10-30). Hereda de "Ver Pagos".
  VIEW_RAFFLES: 'viewRaffles',
};

// Permisos nuevos: si el empleado fue creado antes de que existiera el permiso,
// su doc no tiene la llave. En vez de quitarle acceso de golpe, heredamos del
// permiso equivalente anterior hasta que un admin lo edite explícitamente.
const LEGACY_FALLBACK = {
  [PERMISSIONS.VIEW_CATEGORIES]: PERMISSIONS.VIEW_CALENDAR,
  [PERMISSIONS.EDIT_CATEGORIES]: PERMISSIONS.EDIT_CALENDAR,
  [PERMISSIONS.VIEW_CSCC_VENDORS]: PERMISSIONS.VIEW_PAYMENTS,
  [PERMISSIONS.VIEW_RAFFLES]: PERMISSIONS.VIEW_PAYMENTS,
};

export const getCurrentEmployee = () => {
  try {
    const raw = localStorage.getItem('employee');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// Identidad mostrada en el header/nav: empleado logueado o admin legacy
export const getCurrentIdentity = () => {
  const emp = getCurrentEmployee();
  if (emp) {
    return {
      displayName: emp.name || emp.email || 'Empleado',
      email: emp.email || '',
      role: emp.role === 'admin' ? 'Admin' : 'Empleado',
    };
  }
  const adminEmail = localStorage.getItem('adminEmail') || '';
  return {
    displayName: 'Admin',
    email: adminEmail,
    role: 'Admin',
  };
};

export const hasPermission = (permission) => {
  const emp = getCurrentEmployee();
  // Compatibilidad legacy: si hay token pero no hay employee guardado
  // (login de admin), damos acceso completo.
  if (!emp) return Boolean(localStorage.getItem('token'));
  if (emp.role === 'admin') return true;
  const perms = emp.permissions || {};
  if (perms[permission] === undefined && LEGACY_FALLBACK[permission]) {
    return Boolean(perms[LEGACY_FALLBACK[permission]]);
  }
  return Boolean(perms[permission]);
};

// Primera sección que el usuario logueado puede ver (para redirigir tras login
// o cuando le falta permiso para una página). null = no tiene ninguna.
const SECTION_ORDER = [
  { path: '/dashboard/user', permission: PERMISSIONS.VIEW_MEMBERS },
  { path: '/dashboard/reservations', permission: PERMISSIONS.VIEW_CALENDAR },
  { path: '/dashboard/courses', permission: PERMISSIONS.VIEW_CALENDAR },
  { path: '/dashboard/reservation-categories', permission: PERMISSIONS.VIEW_CATEGORIES },
  { path: '/dashboard/events', permission: PERMISSIONS.VIEW_EVENTS },
  { path: '/dashboard/employees', permission: PERMISSIONS.VIEW_EMPLOYEES },
  { path: '/dashboard/configuration', permission: PERMISSIONS.VIEW_CONFIG },
];

export const firstAllowedPath = () => {
  const found = SECTION_ORDER.find((s) => hasPermission(s.permission));
  return found ? found.path : null;
};

function NoAccess() {
  return (
    <Box sx={{ p: 6, textAlign: 'center' }}>
      <Typography variant="h5" sx={{ mb: 1 }}>Sin acceso</Typography>
      <Typography variant="body2" color="text.secondary">
        Tu cuenta de empleado no tiene permisos asignados.
        Pídele a un admin que te asigne permisos en Empleados.
      </Typography>
    </Box>
  );
}

const ProtectedRoute = ({ element, requiredPermission = null }) => {
  const isAuthenticated = localStorage.getItem('token');

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    const fallback = firstAllowedPath();
    if (fallback) return <Navigate to={fallback} replace />;
    return <NoAccess />;
  }

  return element;
};

export default ProtectedRoute;
